import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "../constants";
import { insufficientCreditsFrom } from "../constants/credits";
import { authHeader, getToken } from "../utils/auth";
import { paidMediaSettlementKnown } from "../utils/paidMediaSettlement";
import usePaidAttempt from "./usePaidAttempt";

const POLL_DELAYS_MS = [5000, 10000, 15000, 30000];
const MAX_POLL_ATTEMPTS = 120;
const ACTIVE_STATUSES = new Set([
  "queued",
  "requested",
  "selecting",
  "generating",
  "reviewing",
  "stitching",
  "processing",
]);

/** @param {unknown} value */
const recordOrNull = (value) =>
  value && typeof value === "object" && !Array.isArray(value)
    ? /** @type {Record<string, any>} */ (value)
    : null;

/** @typedef {{default_clip_count: number, max_clip_count: number, daily_clip_limit: number}} VideoGenerationLimits */

/** @type {Readonly<VideoGenerationLimits>} */
export const DEFAULT_VIDEO_GENERATION_LIMITS = Object.freeze({
  default_clip_count: 1,
  max_clip_count: 1,
  daily_clip_limit: 1,
});

/** @param {unknown} value */
const normalizeGenerationLimits = (value) => {
  const limits = recordOrNull(value);
  if (!limits) return null;
  const rawDaily = Number(limits.daily_clip_limit);
  const dailyClipLimit = Number.isInteger(rawDaily) ? Math.max(1, Math.min(125, rawDaily)) : 1;
  const rawMax = Number(limits.max_clip_count);
  const maxClipCount = Number.isInteger(rawMax)
    ? Math.max(1, Math.min(5, dailyClipLimit, rawMax))
    : 1;
  const rawDefault = Number(limits.default_clip_count);
  const defaultClipCount = Number.isInteger(rawDefault)
    ? Math.max(1, Math.min(maxClipCount, rawDefault))
    : 1;
  return {
    default_clip_count: defaultClipCount,
    max_clip_count: maxClipCount,
    daily_clip_limit: dailyClipLimit,
  };
};

/** @typedef {{cost: number, free_redo_available: boolean, accepts_expected_cost?: boolean}} VideoCredits */

/**
 * The owner's quote block: one price for the finished video, however many
 * clips it takes to make (Peter, 2026-09-17 — one clip is not a usable
 * walkthrough, so the old per-clip price charged for a thing nobody wants).
 * The first redo of a delivered video is still free. Absent for legacy
 * listings — null then, and the card shows no price at all.
 * @param {unknown} value
 * @returns {VideoCredits | null}
 */
export const normalizeVideoCredits = (value) => {
  const credits = recordOrNull(value);
  if (!credits) return null;
  // `cost` is the field; `cost_per_clip` is read only so a payload served by
  // an API instance still mid-deploy is understood. Both carry the whole
  // price, so neither can be multiplied into a wrong quote.
  const cost = Number(credits.cost ?? credits.cost_per_clip);
  if (!Number.isFinite(cost) || cost < 0) return null;
  return {
    cost,
    free_redo_available: credits.free_redo_available === true,
    // The API refuses a start whose confirmed price (expected_cost) is not
    // what it would charge. Only sent to an API that says it understands it.
    ...(credits.accepts_expected_cost === true ? { accepts_expected_cost: true } : {}),
  };
};

/**
 * The price the owner is confirming, for the start request. The quote shown
 * is the whole-video price, or 0 when the free redo covers it.
 * @param {VideoCredits | null | undefined} credits
 * @returns {{expected_cost?: number}}
 */
export const confirmedVideoCost = (credits) =>
  credits?.accepts_expected_cost === true && Number.isSafeInteger(credits.cost) && credits.cost >= 0
    ? { expected_cost: credits.free_redo_available ? 0 : credits.cost }
    : {};

/**
 * Tolerate the planned envelope as well as a direct preview payload during
 * rollout. URLs stay in React memory only; callers must refresh before a
 * download or share action.
 * @param {unknown} payload
 * @param {boolean} [fallbackEnabled]
 */
export const normalizeVideoPreviewResponse = (payload, fallbackEnabled = false) => {
  const data = recordOrNull(payload) || {};
  const nestedEnvelope = recordOrNull(data.video_preview);
  const credits = normalizeVideoCredits(data.credits ?? nestedEnvelope?.credits);
  const directPreview = recordOrNull(data.preview);
  const nestedPreview = recordOrNull(nestedEnvelope?.preview);
  const hasDirectPreviewField = Object.prototype.hasOwnProperty.call(data, "preview");
  const hasNestedPreviewField = Object.prototype.hasOwnProperty.call(
    nestedEnvelope || {},
    "preview"
  );
  const preview =
    directPreview ||
    nestedPreview ||
    (!hasNestedPreviewField && nestedEnvelope?.status ? nestedEnvelope : null) ||
    (!hasDirectPreviewField && (data.uuid || (data.status && data.status !== "ok")) ? data : null);
  const explicitEnabled =
    typeof data.enabled === "boolean"
      ? data.enabled
      : typeof nestedEnvelope?.enabled === "boolean"
        ? nestedEnvelope.enabled
        : null;
  const generationLimits = normalizeGenerationLimits(
    data.generation_limits || nestedEnvelope?.generation_limits
  );

  return {
    // Action responses intentionally omit the feature flag. Preserve the last
    // authoritative GET value instead of inferring that generation is enabled
    // merely because an existing preview is still reviewable/readable.
    enabled: explicitEnabled === null ? fallbackEnabled : explicitEnabled,
    ...(typeof (data.ai_enabled ?? nestedEnvelope?.ai_enabled) === "boolean"
      ? { aiEnabled: data.ai_enabled ?? nestedEnvelope?.ai_enabled }
      : {}),
    // AI motion briefly paused (photo motion still works): the card says so.
    ...(typeof (data.ai_paused ?? nestedEnvelope?.ai_paused) === "boolean"
      ? { aiPaused: data.ai_paused ?? nestedEnvelope?.ai_paused }
      : {}),
    preview,
    generationLimits,
    credits,
  };
};

/** @param {Record<string, any> | null | undefined} preview */
export const videoPreviewStatus = (preview) =>
  typeof preview?.status === "string" ? preview.status.trim().toLowerCase() : "not_requested";

/** @param {Record<string, any> | null | undefined} preview */
export const approvedFinalVideo = (preview) => {
  if (!preview || preview.is_stale === true || videoPreviewStatus(preview) !== "approved") {
    return null;
  }
  const finalVideo = recordOrNull(preview.final_video);
  return finalVideo?.available === true && typeof finalVideo.play_url === "string"
    ? finalVideo
    : null;
};

/** @param {unknown} value */
const boundedConcernText = (value) =>
  typeof value === "string" && value.trim() ? value.trim().slice(0, 500) : "";

/**
 * Owner-only unverified-detail notes attached to an awaiting-review preview.
 * Public/shared payloads never include concerns; render these only in the
 * owner variant. Every field is re-bounded client-side before display.
 * @param {Record<string, any> | null | undefined} preview
 */
export const videoPreviewConcerns = (preview) => {
  if (!Array.isArray(preview?.concerns)) return [];
  return preview.concerns
    .slice(0, 6)
    .map((item) => {
      const record = recordOrNull(item);
      if (!record) return null;
      const summary = boundedConcernText(record.summary);
      const rawDetails = Array.isArray(record.details) ? record.details : record.uncertainties;
      const details = Array.isArray(rawDetails)
        ? rawDetails.map(boundedConcernText).filter(Boolean).slice(0, 20)
        : [];
      const coverageNotes = boundedConcernText(record.coverage_notes);
      if (!summary && details.length === 0 && !coverageNotes) return null;
      const ordinal = Number(record.ordinal);
      return {
        ordinal: Number.isInteger(ordinal) && ordinal >= 1 && ordinal <= 6 ? ordinal : null,
        // "flagged": the automated review found concrete discrepancies.
        // "unverified": it could not verify the clip. Both ship to the owner.
        kind: record.kind === "flagged" ? "flagged" : "unverified",
        summary,
        details,
        coverageNotes,
      };
    })
    .filter((concern) => concern !== null);
};

/** @param {Record<string, any> | null | undefined} preview */
const shouldPollPreview = (preview) => {
  const status = videoPreviewStatus(preview);
  if (ACTIVE_STATUSES.has(status)) return true;
  const finalVideo = recordOrNull(preview?.final_video);
  return (status === "awaiting_review" || status === "approved") && finalVideo?.available !== true;
};

/** @param {unknown} error @param {string} fallback */
const safeVideoError = (error, fallback) => {
  const typed =
    /** @type {{response?: {status?: number, data?: {error?: unknown, code?: unknown}}}} */ (error);
  if (typed.response?.status === 401) return "Your session expired. Sign in again, then retry.";
  if (typed.response?.status === 403) {
    return "Only the listing owner can manage this walkthrough preview.";
  }
  const apiError = typed.response?.data?.error;
  // The AI pause is a 503 with its own plain words; every other 503 keeps
  // the generic message.
  const pauseRefusal =
    Number(typed.response?.status) === 503 && typed.response?.data?.code === "video_ai_paused";
  if (
    ([400, 409, 422, 429].includes(Number(typed.response?.status)) || pauseRefusal) &&
    typeof apiError === "string" &&
    apiError.length > 0 &&
    apiError.length <= 240
  ) {
    return apiError;
  }
  return fallback;
};

/**
 * Owner-only video preview state. Polling deliberately backs off and stops
 * after a bounded number of attempts; generation continues server-side when
 * the page closes or this client stops polling.
 * @param {{uuid: string, active: boolean}} options
 */
const useVideoPreview = ({ uuid, active }) => {
  const activeUuidRef = useRef(uuid);
  activeUuidRef.current = uuid;
  const token = getToken();
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );
  const [authorizedOwner, setAuthorizedOwner] = useState("");
  const ownerKey = `${uuid}:${token || ""}`;
  const paidRef = useRef(/** @type {any} */ (null));
  const exactPreviewRef = useRef(/** @type {string | null} */ (null));
  const snapshotRef = useRef(
    /** @type {ReturnType<typeof normalizeVideoPreviewResponse> | null} */ (null)
  );
  const [startSettlement, setStartSettlement] = useState(/** @type {any} */ (null));
  const pollAttemptRef = useRef(0);
  const [phase, setPhase] = useState("idle");
  const [enabled, setEnabled] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiPaused, setAiPaused] = useState(false);
  const [preview, setPreview] = useState(/** @type {Record<string, any> | null} */ (null));
  const [generationLimits, setGenerationLimits] = useState(
    /** @type {Readonly<VideoGenerationLimits> | null} */ (null)
  );
  const [error, setError] = useState("");
  const [action, setAction] = useState(/** @type {string | null} */ (null));
  const [pollRevision, setPollRevision] = useState(0);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  // Pricing v2: the owner's quote (null for legacy listings) and, when a
  // generate 409ed on the balance, the server's block for the paywall sheet.
  const [credits, setCredits] = useState(/** @type {VideoCredits | null} */ (null));
  const [creditsBlock, setCreditsBlock] = useState(
    /** @type {{balance: number, cost: number, action: string} | null} */ (null)
  );

  /**
   * @param {unknown} payload
   * @param {boolean} fallbackEnabled
   */
  const applyPayload = useCallback((/** @type {unknown} */ payload, fallbackEnabled = false) => {
    const normalized = normalizeVideoPreviewResponse(payload, fallbackEnabled);
    snapshotRef.current = normalized;
    setEnabled(normalized.enabled);
    if (typeof normalized.aiEnabled === "boolean") setAiEnabled(normalized.aiEnabled);
    if (typeof normalized.aiPaused === "boolean") setAiPaused(normalized.aiPaused);
    setPreview(normalized.preview);
    if (normalized.generationLimits) setGenerationLimits(normalized.generationLimits);
    // The quote rides every owner response for a metered listing; an
    // acknowledgement-only action response keeps the last one.
    if (normalized.credits) setCredits(normalized.credits);
    setPhase(normalized.enabled || normalized.preview ? "ready" : "disabled");
    setPollRevision((revision) => revision + 1);
    return normalized;
  }, []);

  const paid = usePaidAttempt({
    scope: { action: "video", targetUuid: uuid },
    enabled: active && !!token && authorizedOwner === ownerKey,
    onAccepted: async (receipt) => {
      const requestToken = getToken();
      const previewId = receipt.attempt.video_preview_id;
      if (!previewId) throw new Error("The accepted preview identity is unavailable.");
      const response = await axios.get(`${API_URL}/computations/${uuid}/video-preview/`, {
        headers: authHeader(),
        params: { video_preview_id: previewId },
        timeout: 15000,
      });
      if (!mountedRef.current || activeUuidRef.current !== uuid || getToken() !== requestToken)
        throw new Error("The owner session changed.");
      const normalized = normalizeVideoPreviewResponse(response.data, enabled);
      if ((normalized.preview?.uuid || normalized.preview?.id) !== previewId)
        throw new Error("The exact preview could not be confirmed.");
      exactPreviewRef.current = previewId;
      setStartSettlement(receipt.settlement);
      setError("");
      setCreditsBlock(null);
      applyPayload(response.data, enabled);
      if (
        receipt.replacement_chain_complete === true &&
        ["completed", "failed"].includes(receipt.job?.status) &&
        receipt.replacement_jobs.length === 0 &&
        paidMediaSettlementKnown(receipt.settlement)
      )
        paidRef.current?.acknowledge(receipt.attempt.attempt_id);
    },
    onRejected: (receipt) => {
      const response = receipt.attempt.response;
      const rejection = { response: { status: response?.status_code, data: response?.data } };
      // A refusal for the AI pause or a moved price carries the server's
      // current availability and quote: adopt them, so the card offers photo
      // motion at the price it will actually charge rather than leaving the
      // owner on a choice that cannot start.
      const data = recordOrNull(response?.data);
      if (data && ["video_ai_paused", "video_price_changed"].includes(data.code)) {
        if (typeof data.ai_enabled === "boolean") setAiEnabled(data.ai_enabled);
        if (typeof data.ai_paused === "boolean") setAiPaused(data.ai_paused);
        const quote = normalizeVideoCredits(data.credits);
        if (quote) setCredits(quote);
      }
      const block = insufficientCreditsFrom(rejection);
      setCreditsBlock(block || null);
      if (!block) setError(safeVideoError(rejection, "This walkthrough request was not accepted."));
      paidRef.current?.acknowledge(receipt.attempt.attempt_id);
    },
  });
  paidRef.current = paid;
  const submitPaidAttempt = paid.submit;

  const refresh = useCallback(
    async ({ quiet = false } = {}) => {
      const requestUuid = uuid;
      const requestToken = getToken();
      if (paidRef.current?.pending) {
        const receipt = await paidRef.current.check();
        if (!receipt || receipt.attempt.status !== "accepted" || !snapshotRef.current)
          throw new Error("This request’s outcome has not been confirmed.");
        return snapshotRef.current;
      }
      if (!quiet) {
        pollAttemptRef.current = 0;
        setPollTimedOut(false);
        setError("");
      }
      try {
        const response = await axios.get(`${API_URL}/computations/${uuid}/video-preview/`, {
          headers: authHeader(),
          ...(exactPreviewRef.current
            ? { params: { video_preview_id: exactPreviewRef.current } }
            : {}),
          timeout: 15000,
        });
        if (
          !mountedRef.current ||
          activeUuidRef.current !== requestUuid ||
          getToken() !== requestToken
        ) {
          throw Object.assign(new Error("The listing route changed."), { staleRoute: true });
        }
        // Quiet polling keeps the previous state visible while a request is in
        // flight, but a successful response must retire any earlier transient
        // network error instead of leaving a stale warning beside fresh state.
        setAuthorizedOwner(`${requestUuid}:${requestToken || ""}`);
        setError("");
        return applyPayload(response.data, enabled);
      } catch (requestError) {
        const staleRequest = /** @type {{staleRoute?: boolean}} */ (requestError);
        if (activeUuidRef.current !== requestUuid || staleRequest.staleRoute || quiet) {
          throw requestError;
        }
        setError(
          safeVideoError(
            requestError,
            "The walkthrough status could not be refreshed. Check your connection, then try again."
          )
        );
        throw requestError;
      }
    },
    [applyPayload, enabled, uuid]
  );

  useEffect(() => {
    const requestUuid = uuid;
    const requestToken = token;
    let cancelled = false;
    setAuthorizedOwner("");
    exactPreviewRef.current = null;
    snapshotRef.current = null;
    setStartSettlement(null);
    pollAttemptRef.current = 0;
    setPollTimedOut(false);
    setError("");
    setAction(null);
    setEnabled(false);
    setAiEnabled(true);
    setAiPaused(false);
    setPreview(null);
    setGenerationLimits(null);
    setCredits(null);
    setCreditsBlock(null);

    if (!active) {
      setPhase("idle");
      return () => {
        cancelled = true;
      };
    }

    setPhase("loading");
    axios
      .get(`${API_URL}/computations/${uuid}/video-preview/`, {
        headers: authHeader(),
        timeout: 15000,
      })
      .then((response) => {
        if (cancelled || activeUuidRef.current !== requestUuid || getToken() !== requestToken)
          return;
        setAuthorizedOwner(`${requestUuid}:${requestToken || ""}`);
        applyPayload(response.data, false);
      })
      .catch((requestError) => {
        if (cancelled || activeUuidRef.current !== requestUuid || getToken() !== requestToken)
          return;
        // During a rolling deploy the endpoint may not exist yet, and a disabled
        // feature may intentionally answer 404. Both should leave no empty card.
        if (requestError.response?.status === 404) {
          setPhase("disabled");
          setEnabled(false);
          return;
        }
        setPhase("error");
        setError(
          safeVideoError(
            requestError,
            "The walkthrough preview could not be loaded. Your listing package is still available."
          )
        );
      });

    return () => {
      cancelled = true;
    };
  }, [active, applyPayload, token, uuid]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pollRevision deliberately schedules another bounded attempt after a failed refresh.
  useEffect(() => {
    if (!active || (!enabled && !preview) || !shouldPollPreview(preview)) return undefined;
    if (pollAttemptRef.current >= MAX_POLL_ATTEMPTS) {
      setPollTimedOut(true);
      return undefined;
    }
    const delay = POLL_DELAYS_MS[Math.min(pollAttemptRef.current, POLL_DELAYS_MS.length - 1)];
    const requestUuid = uuid;
    const timer = window.setTimeout(() => {
      pollAttemptRef.current += 1;
      refresh({ quiet: true }).catch((pollError) => {
        if (activeUuidRef.current !== requestUuid || pollError?.staleRoute) return;
        setError(
          safeVideoError(
            pollError,
            "Walkthrough status could not be refreshed. Generation continues in the background."
          )
        );
        // A failed refresh still schedules a later bounded attempt.
        setPollRevision((revision) => revision + 1);
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [active, enabled, pollRevision, preview, refresh, uuid]);

  const postAction = useCallback(
    async (
      /** @type {string} */ name,
      /** @type {string} */ path,
      /** @type {Record<string, any>} */ body
    ) => {
      const requestUuid = uuid;
      setAction(name);
      setError("");
      setPollTimedOut(false);
      setCreditsBlock(null);
      try {
        const response = await axios.post(
          `${API_URL}/computations/${uuid}/video-preview/${path}`,
          body,
          { headers: authHeader() }
        );
        if (activeUuidRef.current !== requestUuid) {
          throw Object.assign(new Error("The listing route changed."), { staleRoute: true });
        }
        pollAttemptRef.current = 0;
        const normalized = applyPayload(response.data, enabled);
        // Some accepted-action responses contain only an acknowledgement. Fetch
        // the canonical snapshot immediately rather than guessing its state.
        if (!normalized.preview) return refresh({ quiet: true });
        return normalized;
      } catch (actionError) {
        const staleAction = /** @type {{staleRoute?: boolean}} */ (actionError);
        if (activeUuidRef.current !== requestUuid || staleAction.staleRoute) {
          throw actionError;
        }
        // A short balance is the paywall's business, never a bare red line:
        // detect the fixed 409 contract before the raw-string passthrough.
        const block = insufficientCreditsFrom(actionError);
        if (block) {
          setCreditsBlock(block);
          throw actionError;
        }
        setError(
          safeVideoError(actionError, "That walkthrough action could not be completed. Try again.")
        );
        throw actionError;
      } finally {
        if (activeUuidRef.current === requestUuid) setAction(null);
      }
    },
    [applyPayload, enabled, refresh, uuid]
  );

  const generate = useCallback(
    (
      clipCount = generationLimits?.default_clip_count || 1,
      expectedVersion = 0,
      options = /** @type {Record<string,any>|null} */ (null)
    ) => {
      const effectiveLimits = generationLimits || DEFAULT_VIDEO_GENERATION_LIMITS;
      const boundedClipCount = Math.max(
        1,
        Math.min(
          effectiveLimits.max_clip_count,
          Number(clipCount) || effectiveLimits.default_clip_count
        )
      );
      setError("");
      setCreditsBlock(null);
      if (!paidRef.current?.pending) {
        setStartSettlement(null);
        pollAttemptRef.current = 0;
        setPollTimedOut(false);
      }
      return submitPaidAttempt({
        ...(options || {}),
        requested_clip_count: options?.shot_plan ? options.shot_plan.length : boundedClipCount,
        expected_version: Math.max(1, Number(expectedVersion) || 1),
        acknowledge_human_review: true,
        // The quote on screen is the price confirmed; the API refuses rather
        // than charge a different one (the AI pause flipping, for example).
        ...confirmedVideoCost(credits),
      });
    },
    [credits, generationLimits, submitPaidAttempt]
  );

  const confirmationPayload = useCallback(
    (expectedVersion = 0) => ({
      video_preview_id: preview?.uuid || preview?.id || preview?.video_preview_id || "",
      expected_version: Math.max(
        1,
        Number(expectedVersion) ||
          Number(preview?.document_version) ||
          Number(preview?.source_document_version) ||
          1
      ),
      human_confirmation: true,
    }),
    [preview]
  );

  const approve = useCallback(
    (expectedVersion = 0) =>
      postAction("approve", "approve/", confirmationPayload(expectedVersion)),
    [confirmationPayload, postAction]
  );

  const reject = useCallback(
    (expectedVersion = 0) => postAction("reject", "reject/", confirmationPayload(expectedVersion)),
    [confirmationPayload, postAction]
  );

  const dismissCreditsBlock = useCallback(() => setCreditsBlock(null), []);

  return {
    phase,
    enabled,
    aiEnabled,
    aiPaused,
    preview,
    generationLimits,
    credits,
    creditsBlock,
    dismissCreditsBlock,
    error: paid.error || error,
    action: paid.busy ? "generate" : action,
    startPending: !!paid.pending,
    startChecking: paid.busy,
    startMessage:
      paid.receipt?.attempt.status === "accepted"
        ? "This request was accepted. Checking its progress does not start another video."
        : "We haven’t confirmed this request’s outcome yet. Check its status before starting another video.",
    startSettlement,
    checkStartStatus: paid.check,
    pollTimedOut,
    refresh,
    generate,
    approve,
    reject,
  };
};

export default useVideoPreview;
